import { type IHeaderParams } from "ag-grid-community";
import { clsx } from "clsx";
import { remove } from "lodash-es";
import type { MaterialSymbol } from "material-symbols";
import { Component, createRef, type FC, type ReactElement, type ReactNode, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HStack, TriggerButton, TriggerButtonIntent, TriggerButtonSize } from "widgets";

import { isProfileFilterColumn } from "@/app-utils/filter.ts";
import { type IRsqlComparisonExpr, parse, SEARCH_OPERATORS } from "@/app-utils/rsql.ts";
import { type InputTableQuerySpec, type SortData } from "@/graphql.ts";
import {
  getCellExpansionKey,
  getTableDataSlice,
  type ITableColumn,
  setExpansionPath,
} from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { CELL_EXPANSION, SORT_ORDER_ASC, SORT_ORDER_DESC } from "@/settings/settings.ts";
import { type ICustomColDef } from "@/ui-modules/core/grid/DataTable.tsx";
import { type IGridContextProps } from "@/ui-modules/core/grid/InMemoryTableWithSelectionModel.tsx";
import { TooltipController } from "@/ui-modules/core/overlays/components/OverlayControllers.tsx";
import { SampleEditingMenuTrigger } from "@/ui-modules/playbooks/sample/SampleGridEditingMenu.tsx";

import style from "../../styles/GridHeader.module.scss";

export interface IHeaderCellProps {
  hideMenu?: boolean;
  onClickEditButtonCallback?: (columnId: number) => void;
  wrapColumnHeaders?: boolean;

  onColumnSortClick?: (columnField: string, sortOrder: string, multiSort: boolean) => void;
  onRemoveFilterClick?: (columnField: string) => void;
  onFilter?: (columnField: string, filterValue: string) => void;
  onMenuButtonClicked?: (columnMenuData: any) => void;
  // onHeaderResize?: (size: number) => void;
  onToggleHeaderLabelWrapping?: (wrapHeaderLabel: boolean) => void;
  getGridColumns?: () => ITableColumn[];

  sampleEditingMenu?: boolean;
}

class HeaderCell extends Component<IHeaderCellProps & IHeaderParams> {
  // the wrapper element of the menu trigger button, that we will give as parameter
  // to the ColumnMenu component
  private menuWrapperRef = createRef<HTMLDivElement>();

  constructor(props: IHeaderCellProps & IHeaderParams) {
    super(props);
    this.handleColumnSortClick = this.handleColumnSortClick.bind(this);
    this.handleRemoveFilterClick = this.handleRemoveFilterClick.bind(this);
    this.handleMenuClick = this.handleMenuClick.bind(this);
    this.createMenuToggle = this.createMenuToggle.bind(this);
    this.getTableColumn = this.getTableColumn.bind(this);
  }

  handleRemoveFilterClick() {
    const columnDef: ICustomColDef = this.props.column.getColDef();
    this.props.onRemoveFilterClick(columnDef.field);
  }

  handleColumnSortClick = (event) => {
    if (!this.props.onColumnSortClick) {
      this.props.progressSort(event.shiftKey);
      return;
    }

    const multiSort: boolean = event.shiftKey;
    const columnDef: ICustomColDef = this.props.column.getColDef();
    const tableColumn: ITableColumn = this.getTableColumn(columnDef);
    const sort: SortData = tableColumn ? tableColumn.sort : columnDef.sorting;

    // Determine new sort order
    let newSortOrder: string;
    if (!sort) {
      newSortOrder = SORT_ORDER_ASC;
    } else if (sort.direction === SORT_ORDER_ASC) {
      newSortOrder = SORT_ORDER_DESC;
    } else if (sort.direction === SORT_ORDER_DESC) {
      newSortOrder = null;
    } else {
      newSortOrder = SORT_ORDER_ASC;
    }

    this.props.onColumnSortClick(columnDef.field, newSortOrder, multiSort);
  };

  handleMenuClick = (_event: any) => {
    const columnDef: ICustomColDef = this.props.column.getColDef();
    // we provide the column definition and trigger DOM element for positioning the menu accordingly
    this.props.onMenuButtonClicked({
      activeColumn: this.getTableColumn(columnDef),
      menuTarget: this.menuWrapperRef,
    });
  };

  getTableColumn(columnDef: ICustomColDef): ITableColumn {
    if (this.props.getGridColumns) {
      const columns: ITableColumn[] = this.props.getGridColumns();
      return columns?.find((col: ITableColumn) => col.field === columnDef.field);
    }
  }

  isSortable = (tableColumn, columnDef) => {
    const sortableByColumnDefinition: boolean = tableColumn ? tableColumn.sortable : columnDef.sortable;
    return !(sortableByColumnDefinition === false); // meaning undefined leads to sortable = true
  };

  /**
   * This method should be removed, once the profile filtering and the enabling of analysis group members is completely decoupled.
   * At the moment, there is still the IN operator in the table. however, the IN operator does not filter any rows.
   * Instead, it influences the display of the variants track. -> it should be removed from the table with SEL6-1162.
   * @param tableColumn
   * @param columnDef
   */
  shouldShowFilterSymbol = (tableColumn: ITableColumn, columnDef: ICustomColDef) => {
    if (tableColumn) {
      const parsedFilter = parse(tableColumn.filterExpression);
      if (parsedFilter != null && isProfileFilterColumn(tableColumn.field) && parsedFilter.hasOwnProperty("operator")) {
        return (parsedFilter as IRsqlComparisonExpr).operator !== SEARCH_OPERATORS.IN;
      }
      return tableColumn.filterExpression;
    }
    return columnDef.filter;
  };

  render() {
    const { column } = this.props;

    const columnDef: ICustomColDef = column.getColDef();
    const headerName: string = columnDef.headerName;
    const tableColumn: ITableColumn = this.getTableColumn(columnDef);
    const sort: SortData = tableColumn ? tableColumn.sort : columnDef.sorting;
    const sortable: boolean = this.isSortable(tableColumn, columnDef);
    const filter: any = this.shouldShowFilterSymbol(tableColumn, columnDef);

    // if this.props.getGridColumns is not defined, we are in the case of in memory table, using the native sorting mechanism
    const isMultiSortActive = this.props.getGridColumns
      ? this.props.getGridColumns().filter((c: ITableColumn) => !!c.sort).length > 1
      : this.props.columnApi.getColumns().filter((col) => col.getSort()).length > 1;

    const multiSortPriority = sort?.direction && isMultiSortActive ? sort.priority || 1 : null;
    const tooltip: string = tableColumn?.groupName ? `${tableColumn.groupName} - ${headerName}` : `${headerName}`;

    const headerLabelClassNames: string = clsx(style.headerTitle, {
      [style.clickableHeaderCell]: sortable,
      [style.ellipsed]: !this.props.wrapColumnHeaders,
      [style.wrapped]: this.props.wrapColumnHeaders,
    });

    // create the label and sorting/filter icons
    const headerLabel: ReactNode = (
      <TooltipController content={tooltip}>
        {(tooltipTriggerProps) => (
          <div
            className={headerLabelClassNames}
            onPointerEnter={tooltipTriggerProps.onPointerEnter}
            onPointerLeave={tooltipTriggerProps.onPointerLeave}
            ref={tooltipTriggerProps.ref}
            onClick={
              sortable
                ? this.props.onColumnSortClick
                  ? this.handleColumnSortClick
                  : (event) => this.props.progressSort(event.shiftKey)
                : undefined
            }
          >
            {headerName}
          </div>
        )}
      </TooltipController>
    );

    let sortIcon: MaterialSymbol | undefined;
    let sortDirection: string | undefined;
    if (sort?.direction?.toLowerCase() === SORT_ORDER_ASC.toLowerCase()) {
      sortIcon = "arrow_upward";
      sortDirection = "up";
    } else if (sort?.direction?.toLowerCase() === SORT_ORDER_DESC.toLowerCase()) {
      sortIcon = "arrow_downward";
      sortDirection = "down";
    }

    const sorting: ReactElement = sort?.direction && (
      <TriggerButton
        icon={sortIcon}
        label={"Sort"}
        size={TriggerButtonSize.Small}
        onPress={this.handleColumnSortClick}
        badge={multiSortPriority}
        data-sort={sortDirection}
      />
    );

    const filtering: ReactElement = filter && (
      <TriggerButton
        icon={"filter_alt_off"}
        label={"Remove Filter"}
        size={TriggerButtonSize.Small}
        onPress={this.handleRemoveFilterClick}
      />
    );

    const editButton: ReactElement =
      this.props.onClickEditButtonCallback && columnDef.editable ? (
        <div className={clsx(style.dropdownCellHeaderMenu)}>
          <TriggerButton
            tooltip={`Edit ${headerName} settings`}
            icon={"edit"}
            label={"Menu"}
            size={TriggerButtonSize.Small}
            onPress={() => this.props.onClickEditButtonCallback(Number(columnDef.colId))}
          />
        </div>
      ) : null;

    const menuToggle = !this.props.hideMenu && this.createMenuToggle();
    const sampleEditingMenu = this.props.sampleEditingMenu && this.props.column.getColDef().editable && (
      <div className={clsx(style.dropdownCellHeaderMenu)}>
        <SampleEditingMenuTrigger {...this.props} />
      </div>
    );
    return (
      <div>
        <GenericHeaderCell
          headerLabel={headerLabel}
          sortingIcon={sorting}
          filterIcon={filtering}
          button={sampleEditingMenu || editButton || menuToggle}
          {...this.props}
        />
        <TooltipController content={"Click to toggle the label wrapping"}>
          {(tooltipTriggerProps) => (
            <div
              ref={tooltipTriggerProps.ref}
              onPointerEnter={tooltipTriggerProps.onPointerEnter}
              onPointerLeave={tooltipTriggerProps.onPointerLeave}
            >
              <div className="ag-header-row-resize" onMouseDown={this.toggleHeaderLabelWrapping} />
            </div>
          )}
        </TooltipController>
      </div>
    );
  }

  toggleHeaderLabelWrapping = () => {
    this.props.onToggleHeaderLabelWrapping?.(!this.props.wrapColumnHeaders);
  };

  createMenuToggle() {
    return (
      <div ref={this.menuWrapperRef} className={clsx(style.dropdownCellHeaderMenu)}>
        <span id="table-page-column-tools" className="hotspot-beacon" />
        <TriggerButton
          icon={"more_vert"}
          label={"Column Actions"}
          size={TriggerButtonSize.Small}
          onPress={this.handleMenuClick}
        />
      </div>
    );
  }
}

export default HeaderCell;

interface GenericHeaderCellProps extends IHeaderCellProps, IHeaderParams {
  headerLabel: ReactNode;
  sortingIcon: ReactNode;
  filterIcon: ReactNode;
  button: ReactNode;
}

const GenericHeaderCell: FC<GenericHeaderCellProps> = (props) => {
  const { headerLabel, sortingIcon, filterIcon, button } = props;
  const context: IGridContextProps = props.context;

  const expandableField: string = useMemo<string>(() => {
    if (!context?.tableSpec) {
      return null;
    }
    const column: ICustomColDef = props.column.getUserProvidedColDef();
    if (column.cellExpansion === CELL_EXPANSION.STANDALONE || column.cellExpansion === CELL_EXPANSION.CONTEXT) {
      return getCellExpansionKey(column);
    }
  }, []);

  return (
    <HStack
      spacing={1}
      style={{
        width: "100%",
        height: "100%",
        minHeight: 27,
      }}
    >
      {headerLabel}
      {expandableField && <ExpansionIconCell tableQuerySpec={context?.tableSpec} expandableField={expandableField} />}
      {sortingIcon}
      {filterIcon}
      {button}
    </HStack>
  );
};

interface HeaderGroupExpansionIconCellProps {
  tableQuerySpec: InputTableQuerySpec;
  expandableField: string;
}

export const ExpansionIconCell: FC<HeaderGroupExpansionIconCellProps> = (props) => {
  const { tableQuerySpec, expandableField } = props;

  const dispatch = useDispatch<ReduxDispatch>();

  const expansionPath = useSelector<IAppState, string[]>(
    (state) => getTableDataSlice(state.tableData, tableQuerySpec)?.expansionPath,
  );

  const isColExpanded = useMemo<boolean>(() => {
    return !!expansionPath.find((expansionKey) => expansionKey === expandableField);
  }, [expandableField, expansionPath]);

  const handleToggleExpand = useCallback(() => {
    const updatedExpansionPath = [...expansionPath];
    if (isColExpanded) {
      remove(updatedExpansionPath, (key) => key === expandableField);
    } else {
      updatedExpansionPath.push(expandableField);
    }

    dispatch(setExpansionPath(tableQuerySpec, updatedExpansionPath));
  }, [expansionPath, expandableField, isColExpanded]);

  return (
    <TriggerButton
      icon={isColExpanded ? "unfold_less_double" : "unfold_more_double"}
      label={isColExpanded ? "Collapse" : "Expand"}
      size={TriggerButtonSize.Small}
      onPress={handleToggleExpand}
      intent={isColExpanded ? TriggerButtonIntent.Active : TriggerButtonIntent.Secondary}
    />
  );
};
